import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, register, sendRegistrationOtp, verifyRegistrationOtp } from '../lib/api';
import './AccountPage.css';

/* ── SVG icons ── */
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/>
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}

/* ── Password strength ── */
function getStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: 'Too short', color: '#e74c3c' },
    { label: 'Weak',      color: '#e67e22' },
    { label: 'Fair',      color: '#f1c40f' },
    { label: 'Good',      color: '#2ecc71' },
    { label: 'Strong',    color: 'var(--forest)' },
  ];
  return { score, ...map[score] };
}

/* ── OTP input (6 separate boxes) ── */
function OtpInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits = (value + '      ').slice(0, 6).split('');

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) inputs.current[i - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) { inputs.current[i - 1]?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i + 1]?.focus(); return; }
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const arr = (value + '      ').slice(0, 6).split('');
    arr[i] = char;
    onChange(arr.join('').trim());
    if (i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted); inputs.current[Math.min(pasted.length, 5)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="acc-otp-boxes">
      {[0,1,2,3,4,5].map(i => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          className="acc-otp-box"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i].trim()}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

export default function AccountPage({ onLogin, user }) {
  const [tab, setTab]             = useState('login');
  const [showPw, setShowPw]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState('');
  const [error, setError]         = useState('');

  // Signup multi-step: 'form' | 'otp' | 'done'
  const [signupStep, setSignupStep] = useState('form');
  const [regToken, setRegToken]     = useState('');
  const [otpValue, setOtpValue]     = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0); // seconds remaining for resend
  const cooldownRef = useRef(null);

  const [loginForm, setLoginForm] = useState({ email: '', password: '', remember: false });
  const [signupForm, setSignupForm] = useState({
    firstName: '', lastName: '', email: '', password: '', confirm: '', agree: false,
  });
  const navigate = useNavigate();
  const strength = getStrength(signupForm.password);

  // If already logged in, redirect to account details
  React.useEffect(() => {
    if (user) navigate('/account/details');
  }, [user, navigate]);

  // Cleanup cooldown timer on unmount
  React.useEffect(() => () => clearInterval(cooldownRef.current), []);

  const startCooldown = (seconds = 60) => {
    setOtpCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setOtpCooldown(v => {
        if (v <= 1) { clearInterval(cooldownRef.current); return 0; }
        return v - 1;
      });
    }, 1000);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!loginForm.email || !loginForm.password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    try {
      const userData = await login(loginForm.email, loginForm.password);
      if (onLogin) onLogin(userData);
      navigate('/account/orders');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: validate the signup form, then send OTP
  const handleSignupFormSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const { firstName, lastName, email, password, confirm, agree } = signupForm;
    if (!firstName || !lastName || !email || !password || !confirm) { setError('Please fill in all fields.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (strength.score < 2) { setError('Please choose a stronger password.'); return; }
    if (!agree) { setError('Please agree to the terms to continue.'); return; }
    setLoading(true);
    try {
      await sendRegistrationOtp(email);
      setSignupStep('otp');
      setOtpValue('');
      startCooldown(60);
    } catch (err) {
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify OTP, get regToken, then create account
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (otpValue.length < 6) { setError('Please enter the full 6-digit code.'); return; }
    setLoading(true);
    try {
      const { regToken: token } = await verifyRegistrationOtp(signupForm.email, otpValue);
      // Immediately register with the verified token
      const userData = await register(
        signupForm.firstName, signupForm.lastName,
        signupForm.email, signupForm.password, token
      );
      setSuccess('Account created! Welcome to Naturiq.');
      if (onLogin) onLogin(userData);
      setTimeout(() => navigate('/account/orders'), 800);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await sendRegistrationOtp(signupForm.email);
      setOtpValue('');
      startCooldown(60);
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="acc-page">
      {/* ── Form panel ── */}
      <div className="acc-form-panel">
        <div className="acc-form-wrap">
          {/* Tab switcher — hidden during OTP step */}
          {signupStep === 'form' && (
            <div className="acc-tabs">
              <button
                className={`acc-tab${tab === 'login' ? ' acc-tab--active' : ''}`}
                onClick={() => { setTab('login'); setError(''); setSuccess(''); }}
              >Log In</button>
              <button
                className={`acc-tab${tab === 'signup' ? ' acc-tab--active' : ''}`}
                onClick={() => { setTab('signup'); setError(''); setSuccess(''); }}
              >Create Account</button>
            </div>
          )}

          {/* ── Login form ── */}
          {tab === 'login' && (
            <form className="acc-form" onSubmit={handleLoginSubmit} noValidate>
              <h2 className="acc-form__title">Welcome back</h2>
              <p className="acc-form__subtitle">Sign in to manage your orders and wishlist.</p>

              {error && <div className="acc-alert acc-alert--error">{error}</div>}

              <label className="acc-label">
                Email address
                <input
                  className="acc-input"
                  type="email"
                  placeholder="you@email.com"
                  value={loginForm.email}
                  onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                  autoComplete="email"
                />
              </label>

              <label className="acc-label">
                Password
                <div className="acc-input-wrap">
                  <input
                    className="acc-input"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Your password"
                    value={loginForm.password}
                    onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                    autoComplete="current-password"
                  />
                  <button type="button" className="acc-eye" onClick={() => setShowPw(v => !v)} aria-label="Toggle password">
                    <EyeIcon open={showPw} />
                  </button>
                </div>
              </label>

              <div className="acc-row">
                <label className="acc-check">
                  <input
                    type="checkbox"
                    checked={loginForm.remember}
                    onChange={e => setLoginForm(f => ({ ...f, remember: e.target.checked }))}
                  />
                  <span className="acc-check__box" />
                  Remember me
                </label>
                <Link to="/forgot-password" className="acc-forgot">Forgot password?</Link>
              </div>

              <button className="acc-btn acc-btn--primary" type="submit" disabled={loading}>
                {loading ? <span className="acc-spinner" /> : 'Log In'}
              </button>

              <p className="acc-switch">
                Don't have an account?{' '}
                <button type="button" className="acc-link" onClick={() => setTab('signup')}>
                  Sign up free
                </button>
              </p>
            </form>
          )}

          {/* ── Signup form (step 1: details) ── */}
          {tab === 'signup' && signupStep === 'form' && (
            <form className="acc-form" onSubmit={handleSignupFormSubmit} noValidate>
              <h2 className="acc-form__title">Join Naturiq</h2>
              <p className="acc-form__subtitle">Create your free account in under a minute.</p>

              {error   && <div className="acc-alert acc-alert--error">{error}</div>}
              {success && <div className="acc-alert acc-alert--success">{success}</div>}

              <div className="acc-row acc-row--gap">
                <label className="acc-label acc-label--half">
                  First name
                  <input
                    className="acc-input"
                    type="text"
                    placeholder="Arjun"
                    value={signupForm.firstName}
                    onChange={e => setSignupForm(f => ({ ...f, firstName: e.target.value }))}
                    autoComplete="given-name"
                  />
                </label>
                <label className="acc-label acc-label--half">
                  Last name
                  <input
                    className="acc-input"
                    type="text"
                    placeholder="Sharma"
                    value={signupForm.lastName}
                    onChange={e => setSignupForm(f => ({ ...f, lastName: e.target.value }))}
                    autoComplete="family-name"
                  />
                </label>
              </div>

              <label className="acc-label">
                Email address
                <input
                  className="acc-input"
                  type="email"
                  placeholder="you@email.com"
                  value={signupForm.email}
                  onChange={e => setSignupForm(f => ({ ...f, email: e.target.value }))}
                  autoComplete="email"
                />
              </label>

              <label className="acc-label">
                Password
                <div className="acc-input-wrap">
                  <input
                    className="acc-input"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min 8 characters"
                    value={signupForm.password}
                    onChange={e => setSignupForm(f => ({ ...f, password: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <button type="button" className="acc-eye" onClick={() => setShowPw(v => !v)} aria-label="Toggle password">
                    <EyeIcon open={showPw} />
                  </button>
                </div>
                {signupForm.password && (
                  <div className="acc-strength">
                    <div className="acc-strength__bars">
                      {[1,2,3,4].map(i => (
                        <div
                          key={i}
                          className="acc-strength__bar"
                          style={{ background: i <= strength.score ? strength.color : 'var(--border)' }}
                        />
                      ))}
                    </div>
                    <span className="acc-strength__label" style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                )}
              </label>

              <label className="acc-label">
                Confirm password
                <div className="acc-input-wrap">
                  <input
                    className="acc-input"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    value={signupForm.confirm}
                    onChange={e => setSignupForm(f => ({ ...f, confirm: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <button type="button" className="acc-eye" onClick={() => setShowConfirm(v => !v)} aria-label="Toggle confirm">
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </label>

              <label className="acc-check acc-check--terms">
                <input
                  type="checkbox"
                  checked={signupForm.agree}
                  onChange={e => setSignupForm(f => ({ ...f, agree: e.target.checked }))}
                />
                <span className="acc-check__box" />
                I agree to the{' '}
                <Link to="/terms" className="acc-link">Terms of Service</Link>{' '}
                and{' '}
                <Link to="/privacy" className="acc-link">Privacy Policy</Link>
              </label>

              <button className="acc-btn acc-btn--primary" type="submit" disabled={loading}>
                {loading ? <span className="acc-spinner" /> : 'Continue'}
              </button>

              <p className="acc-switch">
                Already have an account?{' '}
                <button type="button" className="acc-link" onClick={() => setTab('login')}>
                  Log in
                </button>
              </p>
            </form>
          )}

          {/* ── Signup step 2: OTP verification ── */}
          {tab === 'signup' && signupStep === 'otp' && (
            <form className="acc-form" onSubmit={handleOtpSubmit} noValidate>
              <div className="acc-otp-icon">
                <MailIcon />
              </div>
              <h2 className="acc-form__title">Verify your email</h2>
              <p className="acc-form__subtitle">
                We sent a 6-digit code to <strong>{signupForm.email}</strong>.<br />
                Enter it below to complete registration.
              </p>

              {error   && <div className="acc-alert acc-alert--error">{error}</div>}
              {success && <div className="acc-alert acc-alert--success">{success}</div>}

              <OtpInput value={otpValue} onChange={setOtpValue} />

              <button className="acc-btn acc-btn--primary" type="submit" disabled={loading || otpValue.length < 6}>
                {loading ? <span className="acc-spinner" /> : 'Verify & Create Account'}
              </button>

              <p className="acc-switch">
                Didn't receive a code?{' '}
                {otpCooldown > 0 ? (
                  <span className="acc-resend-timer">Resend in {otpCooldown}s</span>
                ) : (
                  <button type="button" className="acc-link" onClick={handleResendOtp} disabled={loading}>
                    Resend code
                  </button>
                )}
              </p>

              <p className="acc-switch" style={{ marginTop: 4 }}>
                Wrong email?{' '}
                <button
                  type="button"
                  className="acc-link"
                  onClick={() => { setSignupStep('form'); setError(''); setOtpValue(''); clearInterval(cooldownRef.current); setOtpCooldown(0); }}
                >
                  Go back
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}